import React, { useId, useState } from "react";
import { Box, ButtonBase, Menu, MenuItem, MenuItemProps, Tooltip } from "@mui/material";
import { Check, ExpandLess, ExpandMore } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { Language } from "@/api/data-contracts";
import { useAvailableLanguages } from "@/datacaches";
import { CommonI18N } from "@/translations";
import { resolveApiLanguage } from "@/app/locale/anonymousLanguage";
import { changeAppLanguage } from "@/app/locale/i18nBundle";
import { LANGUAGE_TO_LNG } from "@/app/locale/languageCodes";
import { LanguageFlag } from "@/app/locale/LanguageFlag";
import {
    VISITOR_LANGUAGE_NAMES,
    visitorLanguageOptions,
    writeVisitorLanguageToAddress
} from "@/app/locale/visitorLanguage";

const CHECK_SIZE = 18;

/**
 * MenuList reads `selected` and injects `autoFocus` and `tabIndex` on its direct children only, so this wrapper
 * names its prop `selected` and hands the injected ones on to the MenuItem; otherwise the keyboard reaches no entry.
 */
type VisitorLanguageOptionProps = Pick<MenuItemProps, "autoFocus" | "tabIndex"> & {
    language: Language;
    selected: boolean;
    onSelect: (language: Language) => void;
};

const VisitorLanguageOption = ({ language, selected, onSelect, ...focusProps }: VisitorLanguageOptionProps) => {
    const lng = LANGUAGE_TO_LNG[language];
    const name = VISITOR_LANGUAGE_NAMES[language];
    return (
        <Tooltip title={name} placement="left">
            <MenuItem
                {...focusProps}
                onClick={() => onSelect(language)}
                selected={selected}
                aria-label={name}
                lang={lng}
                data-cy={`visitor-language-option-${lng}`}
                sx={{ gap: 1, minHeight: 0 }}
            >
                <LanguageFlag language={language} />
                <Box component="span" aria-hidden sx={{ display: "inline-flex", width: CHECK_SIZE }}>
                    {selected && <Check sx={{ fontSize: CHECK_SIZE }} />}
                </Box>
            </MenuItem>
        </Tooltip>
    );
};

/**
 * Language selection of a visitor who is not signed in: shows only flags, the language name in its own
 * spelling is the tooltip and the accessible name. A pick applies without a reload, so values already entered in a
 * form stay, and then stands in the address; the browser stores nothing.
 */
export const VisitorLanguageSwitch = () => {
    const { t, i18n } = useTranslation();
    const [availableLanguages] = useAvailableLanguages();
    const [anchor, setAnchor] = useState<HTMLElement | null>(null);
    const menuId = useId();
    const open = anchor !== null;
    const activeLanguage = resolveApiLanguage(i18n.language);
    const label = t(CommonI18N.header.action.selectLanguage, { language: VISITOR_LANGUAGE_NAMES[activeLanguage] });

    const select = async (language: Language) => {
        setAnchor(null);
        writeVisitorLanguageToAddress(language);
        await changeAppLanguage(language);
    };

    return (
        <>
            <Tooltip title={label}>
                <ButtonBase
                    onClick={(event) => setAnchor(event.currentTarget)}
                    aria-label={label}
                    aria-haspopup="menu"
                    aria-expanded={open}
                    aria-controls={open ? menuId : undefined}
                    data-cy="visitor-language-switch"
                    sx={{ gap: 0.25, px: 0.5, py: 1, borderRadius: 1 }}
                >
                    <LanguageFlag language={activeLanguage} />
                    {open ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                </ButtonBase>
            </Tooltip>
            <Menu id={menuId} anchorEl={anchor} open={open} onClose={() => setAnchor(null)}>
                {visitorLanguageOptions(availableLanguages).map((language) => (
                    <VisitorLanguageOption
                        key={language}
                        language={language}
                        selected={language === activeLanguage}
                        onSelect={(picked) => void select(picked)}
                    />
                ))}
            </Menu>
        </>
    );
};
